"use client";

import React from "react";
import { Box,
  Typography,
  Modal,
  Grid,
  IconButton,
  Button,
  Tooltip,
 } from "@mui/material";
import {
  RemoveShoppingCart,
  AddShoppingCart,
  Close,
} from "@mui/icons-material";
import { useAppTranslate } from "src/hooks/useAppTranslate";
import { GetProductDto } from "src/services/generated-api";
import { useLocale } from "next-intl";
import { buttonStyle } from "./single-product";
import { NUMBER_UNITS_SHOW_LIMITED } from "src/constants/number-of-units-limited";
import getProductPrice, {
  getProductBasePrice,
  productHasActionPrice,
} from "src/utils/get-product-price.util";
import { getCurrencyBasedOnDomain } from "src/utils/domain.util";
import { formatCurrency } from "@configs/currencies";
import { useCartDrawer } from "@contexts/cart-drawer-context";
import { resolveLocalizedName } from "src/utils/resolve-localized-name";
type Props = {
  product: GetProductDto;
  open: boolean;
  onClose: () => void;
  addProduct?: (productId: string, quantity: number) => Promise<void>;
  /** Set an existing line's quantity; 0 removes it. */
  setProductQuantity?: (productId: string, quantity: number) => Promise<void>;
  cartQuantities?: Record<string, number>;
};
const ProductModal = ({
  product,
  open,
  onClose,
  addProduct,
  setProductQuantity,
  cartQuantities,
}: Props) => {
  const translate = useAppTranslate();
  const currentLocale = useLocale();
  const { openCartDrawer } = useCartDrawer();

  // Both handlers below used to be commented-out husks left over from the
  // ProductContext removal, and `quantity` was hardcoded to 0. The modal is the
  // detail view customers open before deciding to buy, so its add button was
  // the most natural one to press and the only one that did nothing at all.
  const quantity = cartQuantities?.[product.id] ?? 0;

  const handleAdd = async () => {
    if (!addProduct || product.nOfUnits <= quantity) return;
    openCartDrawer();
    try {
      await addProduct(product.id, quantity + 1);
    } catch (error) {
      console.error("Failed to sync product addition with server:", error);
    }
  };

  const handleRemove = async () => {
    if (!setProductQuantity || quantity <= 0) return;
    try {
      await setProductQuantity(product.id, quantity - 1);
    } catch (error) {
      console.error("Failed to sync product removal with server:", error);
    }
  };
  let language = currentLocale === "sl" ? "si" : currentLocale || "en";
  language = language.split("-")[0];
  const currency = getCurrencyBasedOnDomain();
  // Rank 0 first, as served by the backend. Index 0 is the featured photo.
  const photos = product.photos ?? [];
  const [selectedPhoto, setSelectedPhoto] = React.useState(0);
  const featuredPhoto = photos[Math.min(selectedPhoto, photos.length - 1)];
  // "si" (not "sl") is the Slovenian key admin-entered product names use —
  // see app/admin/product/edit/[id]/page.tsx, which treats "si" as the
  // default/primary language. `language` above is already normalized to
  // "si" when the visitor's locale is "sl".
  //
  // currentLocale goes first, ahead of language: for a locale like "de-AT",
  // `language` has been split down to "de" (line 76) for use elsewhere in
  // this file, but the admin form seeds product names under the verbatim
  // AVAILABLE_LANGUAGES key "de-AT" — useLocale() returns that exact
  // segment. Trying the split form first would always miss a populated
  // "de-AT" name and silently fall through to the Slovenian one. `language`
  // stays in the list right after it so "sl" visitors still map to "si".
  const productName = resolveLocalizedName(
    product.name as Record<string, string> | undefined,
    [currentLocale, language, "si", "en"]
  );
  // Empty string is fine for the title (renders nothing); alt text must
  // never be empty, since alt="" tells a screen reader the image is
  // decorative, which a product photo is not.
  const productAltName = productName || translate("product.productShowcase");
  // Same priority list as productName. No accessibility trap here (unlike
  // alt text), so an empty result just keeps the pre-existing hardcoded
  // English fallback string rather than needing a translated one.
  const productDescription =
    resolveLocalizedName(
      product.description as Record<string, string> | undefined,
      [currentLocale, language, "si", "en"]
    ) || "No description available.";
  const hasActionPrice = productHasActionPrice(product, currency);
  const basePrice = getProductBasePrice(product, currency);
  const basePriceDivided = basePrice ? basePrice / 100 : 0;
  const price = getProductPrice(product, currency);
  const priceDivided = price ? price / 100 : 0;
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: { xs: "90%", sm: "70%", md: "60%" },
    maxWidth: "90%",
    borderRadius: "8px",
    justifyContent: "center",
    alignItems: "center",
    bgcolor: "background.paper",
    boxShadow: 24,
    p: 4,
    overflowY: "auto",
    maxHeight: "90%",
  };
  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="product-modal-title"
      aria-describedby="product-modal-description"
    >
      <Box sx={style}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            id="product-modal-title"
            variant="h5"
            component="h5"
            sx={{ textAlign: "center", flexGrow: 1, my: 2 }}
          >
            {productName}
          </Typography>
          <IconButton onClick={onClose} aria-label={translate("a11y.close")}>
            <Close />
          </IconButton>
        </Box>
        {photos.length > 0 && (
          <Box>
            <Box
              component="img"
              src={featuredPhoto?.url}
              alt={productAltName}
              sx={{
                width: "100%",
                maxHeight: 420,
                objectFit: "contain",
                borderRadius: "8px",
                display: "block",
              }}
            />
            {photos.length > 1 && (
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  mt: 1.5,
                  overflowX: "auto",
                  pb: 0.5,
                }}
              >
                {photos.map((photo, index) => (
                  <Box
                    key={photo.id ?? index}
                    component="img"
                    src={photo.url}
                    loading="lazy"
                    alt={`${productAltName} (${index + 1}/${photos.length})`}
                    onClick={() => setSelectedPhoto(index)}
                    sx={{
                      width: 72,
                      height: 72,
                      flexShrink: 0,
                      objectFit: "cover",
                      borderRadius: "6px",
                      cursor: "pointer",
                      border: "2px solid",
                      borderColor:
                        index === selectedPhoto ? "primary.main" : "transparent",
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            id="product-modal-description"
            sx={{ textAlign: "center", flexGrow: 1, my: 2 }}
          >
            {productDescription}
          </Typography>
        </Box>
        {product.nOfUnits <= NUMBER_UNITS_SHOW_LIMITED && (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              id="product-modal-description"
              sx={{ textAlign: "center", flexGrow: 1, my: 2 }}
            >
              {translate("product.limitedStock")}: {product.nOfUnits}
            </Typography>
          </Box>
        )}
        <Box>
          {hasActionPrice ? (
            <>
              <Typography
                variant="h6"
                component="span"
                color="text.secondary"
                sx={{ textDecoration: "line-through" }}
              >
                {formatCurrency(basePriceDivided, currency)}
              </Typography>
              <Typography variant="h6" color="text.primary">
                {formatCurrency(priceDivided, currency)}
              </Typography>
            </>
          ) : (
            <>
              <Typography variant="h6" component="span" color="rgba(0,0,0,0)">
                {" . "}
              </Typography>

              <Typography variant="h6" color="text.primary">
                {formatCurrency(priceDivided, currency)}
              </Typography>
            </>
          )}
        </Box>
        <Box display="flex" justifyContent="center" alignItems="center">
          <Grid container
            alignItems="center"
            spacing={1}
            sx={{ width: "100%", maxWidth: "400px" }}
          >
            <Grid sx={{ flex: 1, display: "flex", justifyContent: "center" }}
            >
              {quantity > 0 && (
                <Tooltip title={translate("product.removeFromCart")}>
                  <Button
                    variant="outlined"
                    style={buttonStyle}
                    onClick={handleRemove}
                    sx={{ minWidth: 0 }}
                  >
                    <RemoveShoppingCart />
                  </Button>
                </Tooltip>
              )}
            </Grid>
            <Grid sx={{ flex: 1, display: "flex", justifyContent: "center" }}
            >
              <Typography
                sx={{ margin: "0 8px", fontWeight: "bold", fontSize: "20px" }}
              >
                {quantity}
              </Typography>
            </Grid>
            <Grid sx={{ flex: 1, display: "flex", justifyContent: "center" }}
            >
              <Tooltip
                title={
                  product.nOfUnits <= quantity
                    ? translate("product.maxQuantityReached", "Maximum quantity reached")
                    : translate("product.addToCart")
                }
              >
                {/* span keeps the tooltip working while the button is disabled */}
                <span>
                  <Button
                    variant="outlined"
                    style={buttonStyle}
                    onClick={handleAdd}
                    disabled={!addProduct || product.nOfUnits <= quantity}
                    sx={{ minWidth: 0 }}
                  >
                    <AddShoppingCart fontSize={"large"} />
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
};

export default ProductModal;
